import { LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useFeatureFlags } from "@src/context";
import { useApplicationContext } from "@src/context/ApplicationContext";
import ClientLogsService from "@src/services/client-logs.service";
import {
  sendPortalInvitation,
  startPortalRegistration,
} from "@src/services/portal-registration.service";
import { isUnder18AgeRange } from "@src/utils/scheduling-metadata";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconBrand } from "@uhg-abyss/web/ui/IconBrand";
import { Text } from "@uhg-abyss/web/ui/Text";
import { useState } from "react";

const PortalContentRow = styled("div", {
  display: "flex",
  flexDirection: "row",
  minHeight: "250px",
  variants: {
    isMobile: {
      true: { flexDirection: "column" },
    },
  },
});

const PortalBrandPanel = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  maxWidth: "96px",
  backgroundColor: "$app.color.surface.container.primary",
  borderRadius: "8px 0 0 8px",
  paddingTop: "28px",
});

const PortalDetails = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  gap: "1rem",
  minHeight: "250px",
  padding: "25px",
  variants: {
    isMobile: {
      true: { minHeight: "auto", padding: "24px", alignItems: "stretch" },
    },
  },
});

const ActionRow = styled("div", {
  display: "flex",
  gap: "$web.semantic.spacing.scale.sm",
  width: "100%",
  variants: {
    isMobile: {
      true: { flexDirection: "column" },
    },
  },
});

const PortalAction = styled(Button, {
  flex: "0 1 auto",
  justifyContent: "center",
});

const CardHeading = styled(Text, {
  margin: "0",
  fontWeight: 700,
});

export const PortalRegistrationCard = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { ENABLE_PATIENT_PORTAL_REGISTRATION } = useFeatureFlags();
  const { data } = useApplicationContext();
  const appointmentId = data.appointmentDetails?.appointmentIds?.[0];
  const selectedProvider = data.selectedProvider as
    | { departments?: Array<{ department_id?: unknown }> }
    | undefined;
  // Keep parity with the legacy scheduling payload, which uses the first selected-provider department.
  const selectedProviderDepartmentId = selectedProvider?.departments?.[0]?.department_id;
  const departmentId =
    typeof selectedProviderDepartmentId === "number" ? selectedProviderDepartmentId : undefined;
  const userUuid = data.userUuid;
  const isEligibleAdult = Boolean(data.ageRange) && !isUnder18AgeRange(data.ageRange);
  const [isStarting, setIsStarting] = useState(false);
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [startError, setStartError] = useState<string>();
  const [expiredRegistrationMessage, setExpiredRegistrationMessage] = useState<string>();
  const [invitationError, setInvitationError] = useState<string>();
  const [invitationSent, setInvitationSent] = useState(false);
  let invitationActionLabel = "Email me an invitation";

  if (isSendingInvitation) {
    invitationActionLabel = "Sending invitation...";
  }

  if (invitationSent) {
    invitationActionLabel = "Invitation sent";
  }

  if (
    !ENABLE_PATIENT_PORTAL_REGISTRATION ||
    !isEligibleAdult ||
    data.userRole !== "patient" ||
    !userUuid ||
    !Number.isInteger(appointmentId) ||
    !Number.isInteger(departmentId)
  ) {
    return null;
  }

  const handleStart = async () => {
    if (isStarting || !appointmentId || !departmentId) return;

    setIsStarting(true);
    let portalNavigationStarted = false;
    try {
      setStartError(undefined);
      setExpiredRegistrationMessage(undefined);
      const result = await startPortalRegistration({
        appt_id: appointmentId,
        department_id: departmentId,
        user_uuid: userUuid,
      });
      if (!result.success || !result.authorizeUrl) {
        if (result.registrationExpired) {
          setExpiredRegistrationMessage(
            result.message ||
              "Your portal registration session has expired. We've sent an email invitation so you can complete registration later.",
          );
          return;
        }
        setStartError(result.message || "Unable to start patient portal registration.");
        ClientLogsService.logError(
          userUuid,
          "PortalRegistrationCard.handleStart",
          "Patient portal registration could not be started",
          new Error(result.message || "Unable to start patient portal registration"),
          {
            errorType: "DependencyFailure",
            dependencyName: "PortalRegistrationAPI",
            functionName: "handleStart",
          },
        );
        return;
      }

      ClientLogsService.logWebAction(
        userUuid,
        "PortalRegistrationCard.handleStart",
        "Navigating to Athena portal registration",
      );
      globalThis.location.assign(result.authorizeUrl);
      portalNavigationStarted = true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to start patient portal registration.";
      setStartError(message);
      ClientLogsService.logError(
        userUuid,
        "PortalRegistrationCard.handleStart",
        "Patient portal registration request failed",
        error instanceof Error ? error : new Error(message),
        {
          errorType: "DependencyFailure",
          dependencyName: "PortalRegistrationAPI",
          functionName: "handleStart",
        },
      );
    } finally {
      // Keep the handoff state visible until Athena replaces this page. If the request or
      // navigation fails, the error path restores the action so the patient can retry.
      if (!portalNavigationStarted) {
        setIsStarting(false);
      }
    }
  };

  const handleInvitation = async () => {
    if (isSendingInvitation || !appointmentId || !departmentId) return;

    setIsSendingInvitation(true);
    setInvitationError(undefined);
    try {
      const result = await sendPortalInvitation({
        appt_id: appointmentId,
        department_id: departmentId,
        user_uuid: userUuid,
      });
      if (!result.success) {
        setInvitationError(result.message || "Unable to send the patient portal invitation.");
        return;
      }
      setInvitationSent(true);
      ClientLogsService.logWebAction(
        userUuid,
        "PortalRegistrationCard.handleInvitation",
        "Patient portal invitation requested",
      );
    } catch (error) {
      setInvitationError(
        error instanceof Error ? error.message : "Unable to send the patient portal invitation.",
      );
    } finally {
      setIsSendingInvitation(false);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
        marginTop: isMobile ? "16px" : "32px",
      }}
    >
      {invitationSent || expiredRegistrationMessage ? (
        <Card
          css={{
            width: "100%",
            minHeight: isMobile ? "auto" : "250px",
            "abyss-card-root": { padding: "0 !important" },
          }}
          data-testid="portal-registration-invitation-confirmation"
        >
          <PortalDetails isMobile={isMobile}>
            <CardHeading
              as="div"
              role="heading"
              aria-level={2}
              css={{ fontSize: isMobile ? "20px" : "22px" }}
            >
              Check your email
            </CardHeading>
            <Text aria-live="polite">
              {expiredRegistrationMessage ||
                "Your patient portal invitation email has been sent. Please check your inbox."}
            </Text>
          </PortalDetails>
        </Card>
      ) : (
        <Card
          css={{
            width: "100%",
            minHeight: isMobile ? "auto" : "250px",
            "abyss-card-root": { padding: "0 !important" },
          }}
          data-testid="portal-registration-card"
        >
          <PortalContentRow isMobile={isMobile}>
            {!isMobile && (
              <PortalBrandPanel aria-hidden="true">
                <IconBrand icon="document" size={64} style={{ color: "white" }} />
              </PortalBrandPanel>
            )}
            <PortalDetails isMobile={isMobile}>
              <CardHeading
                as="div"
                role="heading"
                aria-level={2}
                css={{ fontSize: isMobile ? "20px" : "22px" }}
              >
                Set up your patient portal
              </CardHeading>
              <Text>You can create an Athena patient portal account now.</Text>
              <ActionRow isMobile={isMobile}>
                <PortalAction
                  id="obc-portal-registration-start"
                  data-obc-label="Set up patient portal now"
                  type="button"
                  onClick={handleStart}
                  disabled={isStarting}
                >
                  {isStarting ? "Taking you to registration..." : "Set up my portal now"}
                </PortalAction>
                <PortalAction
                  id="obc-portal-registration-invitation"
                  data-obc-label="Email patient portal invitation"
                  type="button"
                  variant="outline"
                  onClick={handleInvitation}
                  disabled={isSendingInvitation || invitationSent}
                >
                  {invitationActionLabel}
                </PortalAction>
              </ActionRow>
              {startError && (
                <Text
                  aria-live="assertive"
                  data-testid="portal-registration-start-error"
                  css={{ fontSize: "14px", fontWeight: 700 }}
                >
                  {startError}
                </Text>
              )}
              {invitationError && (
                <Text
                  aria-live="assertive"
                  data-testid="portal-registration-invitation-error"
                  css={{ fontSize: "14px", fontWeight: 700 }}
                >
                  {invitationError}
                </Text>
              )}
            </PortalDetails>
          </PortalContentRow>
        </Card>
      )}
    </div>
  );
};
